import { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import {
  GIST_COMMENT_MAX,
  GIST_POST_MIN,
  GIST_REPORT_REASONS,
  GIST_THREAD_MAX,
  type GistAction,
  type GistCommentView,
  type GistPostView,
  type GistReportReason,
  type GistThreadReply,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { ago } from '../lib/format';
import { GistPostCard } from './post-card';
import { GistAvatar, GistEmpty, GistText, compact, type GistNav } from './parts';
import { VoicePlayer } from '../chat/voice';
import { GistVoiceDraft, GistVoiceMic, type GistVoice } from './voice';

// a reply and the replies under it
type Node = GistCommentView & { replies: Node[] };
const treeOf = (comments: GistCommentView[]) => {
  const nodes = new Map(comments.map((comment) => [comment.id, { ...comment, replies: [] as Node[] }]));
  const roots: Node[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.replies.push(node);
    else roots.push(node);
  }
  return roots;
};
const countOf = (node: Node): number => node.replies.reduce((sum, reply) => sum + 1 + countOf(reply), 0);

// A post's page, as Lagos: the whole thread joined by a line ("Add another post" under your own), then the
// replies (a reply's replies behind "Show N replies") and the box to reply at the bottom.
export function GistPostPage({ id, reply: focus, nav }: { id: string; reply?: boolean; nav: GistNav }) {
  const [thread, setThread] = useState<GistThreadReply | null>(null);
  const [gone, setGone] = useState(false);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<GistCommentView | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [reporting, setReporting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // a voice note recorded for the reply
  const [voice, setVoice] = useState<GistVoice | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    let live = true;
    api.gist
      .thread(id)
      // a post inside a thread opens the thread's first post (its replies are there)
      .then((reply) => (reply.post.threadId ? api.gist.thread(reply.post.threadId) : reply))
      .then((reply) => live && setThread(reply))
      .catch((error: unknown) => {
        if (!live) return;
        setGone(true);
        notify(describeError(error).message);
      });
    return () => {
      live = false;
    };
  }, [id]);
  useEffect(() => {
    if (focus && thread) box.current?.focus();
  }, [focus, thread]);
  if (gone) return <GistEmpty title="This post is gone" body="It was deleted, or you can't see it." />;
  if (!thread) return <p className="gist-loading">Loading the post…</p>;
  const root = thread.post;
  const replace = (next: GistPostView | null, part: GistPostView) => {
    if (!next && part.id === root.id) return nav.back();
    setThread((now) =>
      now
        ? {
            ...now,
            post: next && next.id === now.post.id ? next : now.post,
            thread: next
              ? now.thread.map((item) => (item.id === next.id ? next : item))
              : now.thread.filter((item) => item.id !== part.id),
          }
        : now,
    );
  };
  const act = async (body: GistAction) => {
    setBusy(true);
    try {
      const reply = await api.gist.act(root.id, body);
      if (reply.message) notify(reply.message);
      if (reply.post) setThread({ ...reply, post: reply.post });
      return true;
    } catch (error) {
      notify(describeError(error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  // words (three characters at least), a voice note, or both
  const words = text.trim();
  const ready = (words.length >= GIST_POST_MIN || (voice !== null && words.length === 0)) && !busy;
  const send = async () => {
    if (!ready) return;
    const body = {
      action: 'comment' as const,
      ...(words ? { text: words } : {}),
      ...(voice ? { voice: voice.id } : {}),
      ...(replyTo ? { parentId: replyTo.id } : {}),
    };
    if (await act(body)) {
      setText('');
      setVoice(null);
      if (replyTo) setOpen((now) => new Set(now).add(replyTo.id));
      setReplyTo(null);
    }
  };
  const row = (comment: Node, depth: number) => {
    const shown = open.has(comment.id);
    const under = countOf(comment);
    return (
      <li key={comment.id} className={`gist-reply${comment.held ? ' held' : ''}`} data-depth={Math.min(depth, 3)}>
        <div className="gist-row">
          <button
            type="button"
            className="gist-face"
            aria-label={`${comment.author.name}'s page`}
            onClick={() => nav.user(comment.author.lifeId, comment.author.name)}
          >
            <GistAvatar author={comment.author} size={32} />
          </button>
          <div className="gist-main-col">
            <header className="gist-head">
              <strong className="gist-name">{comment.author.name}</strong>
              <small>· {ago(comment.at)}</small>
            </header>
            {comment.body && <GistText text={comment.body} onLink={nav.search} />}
            {comment.voice && <VoicePlayer playPath={comment.voice.playPath} durationMs={comment.voice.durationMs} />}
            <span className="gist-reply-actions">
              <button
                type="button"
                aria-pressed={comment.liked}
                aria-label={comment.liked ? 'Unlike the reply' : 'Like the reply'}
                className={comment.liked ? 'on-like' : ''}
                disabled={busy}
                onClick={() => void act({ action: 'comment_like', commentId: comment.id, on: !comment.liked })}
              >
                <Heart aria-hidden="true" fill={comment.liked ? 'currentColor' : 'none'} />{' '}
                {comment.likes ? compact(comment.likes) : ''}
              </button>
              <button
                type="button"
                onClick={() => {
                  setReplyTo(comment);
                  box.current?.focus();
                }}
              >
                Reply
              </button>
              {comment.mine ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void act({ action: 'uncomment', commentId: comment.id })}
                >
                  Remove reply
                </button>
              ) : (
                <button type="button" onClick={() => setReporting(reporting === comment.id ? null : comment.id)}>
                  Report
                </button>
              )}
            </span>
            {reporting === comment.id && (
              <span className="gist-menu" role="group" aria-label="What is wrong with this reply">
                {(Object.keys(GIST_REPORT_REASONS) as GistReportReason[]).map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setReporting(null);
                      void act({ action: 'report_comment', commentId: comment.id, reason });
                    }}
                  >
                    {GIST_REPORT_REASONS[reason]}
                  </button>
                ))}
              </span>
            )}
          </div>
        </div>
        {under > 0 && !shown && (
          <button
            type="button"
            className="gist-link gist-show-replies"
            onClick={() => setOpen((now) => new Set(now).add(comment.id))}
          >
            Show {under === 1 ? '1 reply' : `${under} replies`}
          </button>
        )}
        {shown && <ul className="gist-replies">{comment.replies.map((reply) => row(reply, depth + 1))}</ul>}
      </li>
    );
  };
  const canAdd = root.mine && root.parts < GIST_THREAD_MAX;
  return (
    <div className="gist-post-page">
      <ul className="gist-list gist-thread" aria-label="The thread">
        {thread.thread.map((part, index) => (
          <GistPostCard
            key={part.id}
            post={part}
            nav={nav}
            feed={false}
            line={index < thread.thread.length - 1 || canAdd}
            onChange={(next) => replace(next, part)}
            onGone={() => replace(null, part)}
          />
        ))}
      </ul>
      {canAdd && (
        <button type="button" className="gist-add-part" onClick={() => nav.compose(root)}>
          <span aria-hidden="true">+</span> Add another post
        </button>
      )}
      <section className="gist-replies-wrap" aria-label="Replies">
        {thread.comments.length === 0 && <p className="gist-loading">No replies yet. Be the first to reply.</p>}
        <ul className="gist-replies">{treeOf(thread.comments).map((comment) => row(comment, 0))}</ul>
      </section>
      <div className="gist-reply-box">
        {replyTo && (
          <p className="gist-replying">
            Replying to {replyTo.author.name}{' '}
            <button type="button" aria-label="Reply to the post instead" onClick={() => setReplyTo(null)}>
              ✕
            </button>
          </p>
        )}
        {voice && <GistVoiceDraft voice={voice} onRemove={() => setVoice(null)} />}
        <span className="gist-reply-bar">
          <GistVoiceMic disabled={busy || voice !== null} onVoice={setVoice} />
          <textarea
            ref={box}
            aria-label={replyTo ? `Reply to ${replyTo.author.name}` : 'Post your reply'}
            value={text}
            rows={1}
            maxLength={GIST_COMMENT_MAX}
            placeholder="Post your reply"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button type="button" className="gist-post-button" disabled={!ready} onClick={() => void send()}>
            Reply
          </button>
        </span>
      </div>
    </div>
  );
}
